// Export utilities for Exam Attempts — Excel (XLSX) and PDF
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';

// ── Color palette ──
const COLORS = {
  purple:      'FF6D28D9',
  purpleLight: 'FFEDE9FE',
  purpleBg:    'FFF5F3FF',
  success:     'FF10B981',
  successBg:   'FFECFDF3',
  danger:      'FFEF4444',
  dangerBg:    'FFFEF2F2',
  blue:        'FF3B82F6',
  blueBg:      'FFDBEAFE',
  amber:       'FFD97706',
  amberBg:     'FFFFFBEB',
  orange:      'FFEA580C',
  gray800:     'FF1E293B',
  gray600:     'FF475569',
  gray500:     'FF64748B',
  gray400:     'FF94A3B8',
  gray200:     'FFE2E8F0',
  gray100:     'FFF1F5F9',
  gray50:      'FFF8FAFC',
  white:       'FFFFFFFF',
};

/**
 * Helper: build the data rows used by both exports
 */
function buildExportData(attempts, examTitle, calculateDuration) {
  const rows = attempts.map((attempt, idx) => {
    const maxScore = attempt.max_score || attempt.total_points || 1;
    const earned = attempt.total_score ?? attempt.points_earned ?? 0;
    const note20 = (earned / maxScore) * 20;
    const percentage = attempt.percentage ?? 0;

    return {
      index: idx + 1,
      name: attempt.user_name || 'Utilisateur',
      id: attempt.user_id || '-',
      date: attempt.started_at
        ? new Date(attempt.started_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
        : '-',
      duration: calculateDuration(attempt),
      scorePercent: `${percentage.toFixed(1)}%`,
      scoreRaw: `${earned} / ${maxScore}`,
      note20: parseFloat(note20.toFixed(1)),
      note20Str: note20.toFixed(1),
      result: attempt.passed ? 'Réussi' : 'Échoué',
      passed: attempt.passed,
      infractions: attempt.violations_count || 0,
    };
  });

  // Stats
  const totalAttempts = attempts.length;
  const passed = attempts.filter((a) => a.passed).length;
  const failed = attempts.filter(
    (a) => !a.passed && (a.status === 'graded' || a.status === 'submitted')
  ).length;
  const totalInfractions = attempts.reduce((s, a) => s + (a.violations_count || 0), 0);

  const notes = attempts.map((a) => {
    const mx = a.max_score || a.total_points || 1;
    const er = a.total_score ?? a.points_earned ?? 0;
    return (er / mx) * 20;
  });
  const avgNote = notes.length ? (notes.reduce((a, b) => a + b, 0) / notes.length).toFixed(2) : '0';
  const maxNote = notes.length ? Math.max(...notes).toFixed(1) : '0';
  const minNote = notes.length ? Math.min(...notes).toFixed(1) : '0';
  const successRate = totalAttempts ? ((passed / totalAttempts) * 100).toFixed(1) : '0';

  return {
    rows,
    stats: { totalAttempts, passed, failed, totalInfractions, avgNote, maxNote, minNote, successRate },
  };
}

// ─────────────────────────────────────────────
//  EXCEL EXPORT (ExcelJS — fully styled)
// ─────────────────────────────────────────────
export async function exportToExcel(attempts, examTitle, calculateDuration) {
  const { rows, stats } = buildExportData(attempts, examTitle, calculateDuration);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'DevOpsLearn';
  wb.created = new Date();

  const ws = wb.addWorksheet('Résultats', {
    properties: { defaultRowHeight: 20 },
    views: [{ showGridLines: false }],
  });

  // ── Column widths ──
  ws.columns = [
    { key: 'num',         width: 6 },
    { key: 'candidat',    width: 30 },
    { key: 'date',        width: 16 },
    { key: 'duree',       width: 12 },
    { key: 'scorePct',    width: 12 },
    { key: 'scoreRaw',    width: 14 },
    { key: 'note20',      width: 12 },
    { key: 'resultat',    width: 12 },
    { key: 'infractions', width: 14 },
  ];

  const totalCols = 9; // A..I

  // ── Shared styles ──
  const thinBorder = (color = COLORS.gray200) => ({
    style: 'thin', color: { argb: color }
  });
  const noBorder = { style: 'none' };

  // ───────── ROW 1: Title bar (purple bg) ─────────
  const titleRow = ws.addRow(['']);
  ws.mergeCells('A1:I1');
  const titleCell = ws.getCell('A1');
  titleCell.value = `Résultats d'examen — ${examTitle}`;
  titleCell.font = { bold: true, size: 16, color: { argb: COLORS.white }, name: 'Calibri' };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purple } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  titleRow.height = 38;
  // Apply purple fill to all cells in row 1
  for (let c = 1; c <= totalCols; c++) {
    const cell = ws.getCell(1, c);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purple } };
    cell.border = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };
  }

  // ───────── ROW 2: Date bar ─────────
  const dateStr = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const dateRow = ws.addRow(['']);
  ws.mergeCells('A2:I2');
  const dateCell = ws.getCell('A2');
  dateCell.value = `Date d'export : ${dateStr}  •  ${stats.totalAttempts} candidat(s)`;
  dateCell.font = { size: 10, color: { argb: COLORS.white }, name: 'Calibri', italic: true };
  dateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7C3AED' } };
  dateCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  dateRow.height = 24;
  for (let c = 1; c <= totalCols; c++) {
    const cell = ws.getCell(2, c);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7C3AED' } };
    cell.border = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };
  }

  // ───────── ROW 3: spacer ─────────
  const spacer1 = ws.addRow(['']);
  spacer1.height = 8;

  // ───────── ROW 4: Table headers ─────────
  const headerLabels = ['#', 'Candidat', 'Date', 'Durée', 'Score %', 'Score brut', 'Note /20', 'Résultat', 'Infractions'];
  const headerRow = ws.addRow(headerLabels);
  headerRow.height = 28;
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, size: 10, color: { argb: COLORS.white }, name: 'Calibri' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purple } };
    cell.alignment = { vertical: 'middle', horizontal: colNumber <= 2 ? 'left' : 'center', indent: colNumber === 1 ? 1 : 0 };
    cell.border = {
      top: thinBorder(COLORS.purple),
      bottom: thinBorder(COLORS.purple),
      left: thinBorder(COLORS.purple),
      right: thinBorder(COLORS.purple),
    };
  });

  // ───────── DATA ROWS ─────────
  rows.forEach((r, i) => {
    const dataRow = ws.addRow([
      r.index,
      r.name,
      r.date,
      r.duration,
      r.scorePercent,
      r.scoreRaw,
      r.note20Str,
      r.result,
      r.infractions,
    ]);
    dataRow.height = 24;

    const isEven = i % 2 === 0;
    const bgColor = isEven ? COLORS.gray50 : COLORS.white;

    dataRow.eachCell((cell, colNumber) => {
      cell.font = { size: 10, color: { argb: COLORS.gray800 }, name: 'Calibri' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.alignment = {
        vertical: 'middle',
        horizontal: colNumber <= 2 ? 'left' : 'center',
        indent: colNumber === 1 ? 1 : 0,
      };
      cell.border = {
        top: noBorder,
        bottom: thinBorder(COLORS.gray200),
        left: noBorder,
        right: noBorder,
      };
    });

    // ── Note /20 color coding (col 7) ──
    const noteCell = dataRow.getCell(7);
    noteCell.font = { bold: true, size: 11, name: 'Calibri',
      color: { argb:
        r.note20 >= 16 ? 'FF059669' :
        r.note20 >= 14 ? COLORS.blue :
        r.note20 >= 10 ? COLORS.amber :
        COLORS.danger
      }
    };

    // ── Résultat color (col 8) ──
    const resultCell = dataRow.getCell(8);
    if (r.passed) {
      resultCell.font = { bold: true, size: 10, color: { argb: COLORS.success }, name: 'Calibri' };
      resultCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.successBg } };
    } else {
      resultCell.font = { bold: true, size: 10, color: { argb: COLORS.danger }, name: 'Calibri' };
      resultCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.dangerBg } };
    }

    // ── Infractions color (col 9) ──
    if (r.infractions > 0) {
      const infCell = dataRow.getCell(9);
      infCell.font = { bold: true, size: 10, color: { argb: COLORS.orange }, name: 'Calibri' };
      infCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.amberBg } };
    }
  });

  // ───────── SPACER ─────────
  const spacer2 = ws.addRow(['']);
  spacer2.height = 12;
  const spacer3 = ws.addRow(['']);
  spacer3.height = 6;

  // ───────── STATISTICS SECTION ─────────
  const statsStartRow = ws.rowCount + 1;

  // Stats title row
  const statsTitleRow = ws.addRow(['']);
  ws.mergeCells(`A${statsStartRow}:I${statsStartRow}`);
  const stCell = ws.getCell(`A${statsStartRow}`);
  stCell.value = '📊  Récapitulatif & Statistiques';
  stCell.font = { bold: true, size: 13, color: { argb: COLORS.purple }, name: 'Calibri' };
  stCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purpleBg } };
  stCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  stCell.border = {
    top: thinBorder(COLORS.purple),
    bottom: noBorder,
    left: { style: 'medium', color: { argb: COLORS.purple } },
    right: thinBorder(COLORS.purple),
  };
  statsTitleRow.height = 32;
  for (let c = 2; c <= totalCols; c++) {
    const cell = ws.getCell(statsStartRow, c);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purpleBg } };
    cell.border = {
      top: thinBorder(COLORS.purple),
      bottom: noBorder,
      left: noBorder,
      right: c === totalCols ? thinBorder(COLORS.purple) : noBorder,
    };
  }

  // Stats Row 1: 4 items (value + label pairs stacked)
  const statItems = [
    { label: 'Tentatives', value: String(stats.totalAttempts), color: COLORS.gray800 },
    { label: 'Réussites', value: String(stats.passed), color: COLORS.success },
    { label: 'Échecs', value: String(stats.failed), color: COLORS.danger },
    { label: 'Taux de réussite', value: `${stats.successRate}%`, color: COLORS.purple },
  ];
  const statItems2 = [
    { label: 'Moyenne /20', value: stats.avgNote, color: COLORS.gray800 },
    { label: 'Note max /20', value: stats.maxNote, color: COLORS.success },
    { label: 'Note min /20', value: stats.minNote, color: COLORS.danger },
    { label: 'Total infractions', value: String(stats.totalInfractions), color: COLORS.orange },
  ];

  const writeStatRow = (items, isLast) => {
    // Value row
    const valRowNum = ws.rowCount + 1;
    const valuesArr = [''];
    // Map 4 stats across cols B-C, D-E, F-G, H-I (using col pairs)
    // Use cols: B(2), D(4), F(6), H(8) for values
    const valRow = ws.addRow([]);
    valRow.height = 28;

    const statPositions = [2, 4, 6, 8]; // columns B, D, F, H

    items.forEach((item, idx) => {
      const col = statPositions[idx];
      // Merge 2 columns for each stat
      ws.mergeCells(valRowNum, col, valRowNum, col + 1);
      const cell = ws.getCell(valRowNum, col);
      cell.value = item.value;
      cell.font = { bold: true, size: 18, color: { argb: item.color }, name: 'Calibri' };
      cell.alignment = { vertical: 'bottom', horizontal: 'left', indent: 1 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purpleBg } };
    });

    // Fill remaining cells
    for (let c = 1; c <= totalCols; c++) {
      const cell = ws.getCell(valRowNum, c);
      if (!cell.value && cell.value !== 0) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purpleBg } };
      }
      cell.border = {
        top: noBorder, bottom: noBorder,
        left: c === 1 ? { style: 'medium', color: { argb: COLORS.purple } } : noBorder,
        right: c === totalCols ? thinBorder(COLORS.purple) : noBorder,
      };
    }

    // Label row
    const lblRowNum = ws.rowCount + 1;
    const lblRow = ws.addRow([]);
    lblRow.height = 18;

    items.forEach((item, idx) => {
      const col = statPositions[idx];
      ws.mergeCells(lblRowNum, col, lblRowNum, col + 1);
      const cell = ws.getCell(lblRowNum, col);
      cell.value = item.label;
      cell.font = { size: 9, color: { argb: COLORS.gray500 }, name: 'Calibri' };
      cell.alignment = { vertical: 'top', horizontal: 'left', indent: 1 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purpleBg } };
    });

    for (let c = 1; c <= totalCols; c++) {
      const cell = ws.getCell(lblRowNum, c);
      if (!cell.value) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purpleBg } };
      }
      cell.border = {
        top: noBorder,
        bottom: isLast ? thinBorder(COLORS.purple) : noBorder,
        left: c === 1 ? { style: 'medium', color: { argb: COLORS.purple } } : noBorder,
        right: c === totalCols ? thinBorder(COLORS.purple) : noBorder,
      };
    }
  };

  writeStatRow(statItems, false);

  // Small spacer between stat rows
  const spacerStatRow = ws.addRow([]);
  spacerStatRow.height = 4;
  for (let c = 1; c <= totalCols; c++) {
    const cell = ws.getCell(ws.rowCount, c);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.purpleBg } };
    cell.border = {
      top: noBorder, bottom: noBorder,
      left: c === 1 ? { style: 'medium', color: { argb: COLORS.purple } } : noBorder,
      right: c === totalCols ? thinBorder(COLORS.purple) : noBorder,
    };
  }

  writeStatRow(statItems2, true);

  // ───────── FOOTER ─────────
  const footerSpacer = ws.addRow(['']);
  footerSpacer.height = 10;
  const footerRowNum = ws.rowCount + 1;
  const footerRow = ws.addRow([]);
  ws.mergeCells(`A${footerRowNum}:I${footerRowNum}`);
  const footerCell = ws.getCell(`A${footerRowNum}`);
  footerCell.value = `DevOpsLearn — ${examTitle}  •  Généré le ${dateStr}`;
  footerCell.font = { size: 8, color: { argb: COLORS.gray400 }, name: 'Calibri', italic: true };
  footerCell.alignment = { vertical: 'middle', horizontal: 'center' };
  footerCell.border = {
    top: thinBorder(COLORS.gray200),
    bottom: noBorder, left: noBorder, right: noBorder,
  };

  // ── Save ──
  const fileName = `resultats_${examTitle.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
}

// ─────────────────────────────────────────────
//  PDF EXPORT
// ─────────────────────────────────────────────
export function exportToPDF(attempts, examTitle, calculateDuration) {
  const { rows, stats } = buildExportData(attempts, examTitle, calculateDuration);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // ── Colors ──
  const PRIMARY = [109, 40, 217];      // Purple-700
  const PRIMARY_LIGHT = [237, 233, 254]; // Purple-50
  const SUCCESS = [16, 185, 129];
  const DANGER = [239, 68, 68];
  const GRAY_800 = [30, 41, 59];
  const GRAY_500 = [100, 116, 139];
  const GRAY_200 = [226, 232, 240];
  const WHITE = [255, 255, 255];

  // ── Header banner ──
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageW, 28, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...WHITE);
  doc.text('Résultats d\'examen', 14, 12);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(examTitle, 14, 20);

  // Date top-right
  doc.setFontSize(9);
  const exportDate = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  doc.text(`Exporté le ${exportDate}`, pageW - 14, 12, { align: 'right' });
  doc.text(`${stats.totalAttempts} candidat(s)`, pageW - 14, 19, { align: 'right' });

  // ── Table ──
  const startY = 34;
  const cols = [
    { label: '#',           width: 10,  align: 'center', key: 'index' },
    { label: 'Candidat',    width: 58,  align: 'left',   key: 'name' },
    { label: 'Date',        width: 26,  align: 'center', key: 'date' },
    { label: 'Durée',       width: 20,  align: 'center', key: 'duration' },
    { label: 'Score %',     width: 22,  align: 'center', key: 'scorePercent' },
    { label: 'Score brut',  width: 28,  align: 'center', key: 'scoreRaw' },
    { label: 'Note /20',    width: 22,  align: 'center', key: 'note20Str' },
    { label: 'Résultat',    width: 22,  align: 'center', key: 'result' },
    { label: 'Infractions', width: 24,  align: 'center', key: 'infractions' },
  ];
  const totalTableW = cols.reduce((s, c) => s + c.width, 0);
  const tX = (pageW - totalTableW) / 2;

  const rowHeight = 8;
  const headerHeight = 10;

  // Draw header row
  let curY = startY;
  doc.setFillColor(...PRIMARY);
  doc.rect(tX, curY, totalTableW, headerHeight, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...WHITE);

  let cx = tX;
  cols.forEach((col) => {
    const textX = col.align === 'center' ? cx + col.width / 2 : cx + 3;
    doc.text(col.label, textX, curY + 7, { align: col.align === 'center' ? 'center' : 'left' });
    cx += col.width;
  });

  curY += headerHeight;

  // Draw data rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);

  rows.forEach((row, i) => {
    // Check page overflow
    if (curY + rowHeight > pageH - 50) {
      doc.addPage();
      curY = 14;
      // Re-draw header on new page
      doc.setFillColor(...PRIMARY);
      doc.rect(tX, curY, totalTableW, headerHeight, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...WHITE);
      let hx = tX;
      cols.forEach((col) => {
        const textX = col.align === 'center' ? hx + col.width / 2 : hx + 3;
        doc.text(col.label, textX, curY + 7, { align: col.align === 'center' ? 'center' : 'left' });
        hx += col.width;
      });
      curY += headerHeight;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
    }

    // Alternating row bg
    if (i % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(tX, curY, totalTableW, rowHeight, 'F');
    }

    // Row border
    doc.setDrawColor(...GRAY_200);
    doc.setLineWidth(0.2);
    doc.line(tX, curY + rowHeight, tX + totalTableW, curY + rowHeight);

    let rx = tX;
    cols.forEach((col) => {
      let value = String(row[col.key] ?? '');

      // Colorize result
      if (col.key === 'result') {
        if (value === 'Réussi') {
          doc.setTextColor(...SUCCESS);
          doc.setFont('helvetica', 'bold');
        } else {
          doc.setTextColor(...DANGER);
          doc.setFont('helvetica', 'bold');
        }
      } else if (col.key === 'infractions' && parseInt(value) > 0) {
        doc.setTextColor(...DANGER);
      } else if (col.key === 'note20Str') {
        const noteVal = parseFloat(value);
        if (noteVal >= 16) doc.setTextColor(5, 150, 105);
        else if (noteVal >= 14) doc.setTextColor(59, 130, 246);
        else if (noteVal >= 10) doc.setTextColor(217, 119, 6);
        else doc.setTextColor(...DANGER);
        doc.setFont('helvetica', 'bold');
      } else {
        doc.setTextColor(...GRAY_800);
        doc.setFont('helvetica', 'normal');
      }

      const textX = col.align === 'center' ? rx + col.width / 2 : rx + 3;

      // Truncate long candidate names
      if (col.key === 'name' && value.length > 30) {
        value = value.substring(0, 28) + '…';
      }

      doc.text(value, textX, curY + 5.5, { align: col.align === 'center' ? 'center' : 'left' });
      rx += col.width;
    });

    curY += rowHeight;
  });

  // ── Statistics section ──
  curY += 8;

  // Check if stats fit on current page
  if (curY + 50 > pageH) {
    doc.addPage();
    curY = 14;
  }

  // Stats background card
  const statsW = totalTableW;
  const statsH = 42;
  const statsX = tX;

  // Card background
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(statsX, curY, statsW, statsH, 3, 3, 'F');

  // Card border
  doc.setDrawColor(...GRAY_200);
  doc.setLineWidth(0.3);
  doc.roundedRect(statsX, curY, statsW, statsH, 3, 3, 'S');

  // Purple accent bar
  doc.setFillColor(...PRIMARY);
  doc.rect(statsX, curY, 4, statsH, 'F');

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PRIMARY);
  doc.text('Récapitulatif & Statistiques', statsX + 12, curY + 9);

  // Stats grid: 2 rows x 4 columns
  const statItems = [
    { label: 'Tentatives', value: String(stats.totalAttempts), color: GRAY_800 },
    { label: 'Réussites', value: String(stats.passed), color: SUCCESS },
    { label: 'Échecs', value: String(stats.failed), color: DANGER },
    { label: 'Taux de réussite', value: stats.successRate + '%', color: PRIMARY },
    { label: 'Moyenne /20', value: stats.avgNote, color: GRAY_800 },
    { label: 'Note max /20', value: stats.maxNote, color: SUCCESS },
    { label: 'Note min /20', value: stats.minNote, color: DANGER },
    { label: 'Total infractions', value: String(stats.totalInfractions), color: [234, 88, 12] },
  ];

  const statColW = (statsW - 20) / 4;
  const statStartX = statsX + 12;

  statItems.forEach((item, i) => {
    const col = i % 4;
    const row = Math.floor(i / 4);
    const sx = statStartX + col * statColW;
    const sy = curY + 16 + row * 14;

    // Value
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...item.color);
    doc.text(item.value, sx, sy);

    // Label
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_500);
    doc.text(item.label, sx, sy + 5);
  });

  // ── Footer ──
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_500);
    doc.text(`DevOpsLearn — ${examTitle}`, 14, pageH - 6);
    doc.text(`Page ${p}/${totalPages}`, pageW - 14, pageH - 6, { align: 'right' });

    // Bottom line
    doc.setDrawColor(...GRAY_200);
    doc.setLineWidth(0.3);
    doc.line(14, pageH - 10, pageW - 14, pageH - 10);
  }

  const fileName = `resultats_${examTitle.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName);
}
